<script lang="ts">
import type { DropdownMenuItem, TableData } from '@nuxt/ui';
import type { ColumnOrderState, ColumnPinningState, ExpandedState, HeaderContext, PaginationState, Row } from '@tanstack/vue-table';
import type {
	DataTableBaseAdditionalProps,
	DataTableBaseEmits,
	DataTableBaseProps,
	ExtendedTableColumn,
} from './Base.vue';

export interface DataTableProps<T> extends DataTableBaseProps<T>, DataTableBaseAdditionalProps {
	canSelect?: boolean;
	canSort?: boolean;

	title?: string;
	description?: string;

	pageSizeOptions?: number[];

	bordered?: boolean;

	class?: any;
	hidePagination?: boolean;
	manualSorting?: boolean;
	disabled?: boolean;

	totalItems?: number;
}

export interface DataTableEmits extends DataTableBaseEmits {
	'update:columnOrder': [columnOrder: ColumnOrderState];
	'update:columnPinning': [columnPinning: ColumnPinningState];
}
</script>

<script setup lang="ts" generic="T extends TableData">
import type { Component } from 'vue';
import type { ComponentExposed } from 'vue-component-type-helpers';
import { getPaginationRowModel } from '@tanstack/vue-table';
import { reactiveOmit } from '@vueuse/core';
import { defu } from 'defu';
import { useForwardPropsEmits } from 'reka-ui';
import { upperFirst } from 'scule';
import { computed, h, useSlots, useTemplateRef, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { DButton, DCheckbox, DDataTableBase, DDropdownMenu } from '#components';
import { callOrReturn } from '#layers/base/public';
import { DEFAULT_LISTING_PAGE_SIZE } from '#layers/ui/constants/pagination';
import { mergeUi } from '#layers/ui/utils/merge-ui';
import { buildColumnHeaderItemGroups } from './column-header-menu';

defineOptions({
	inheritAttrs: false,
});

const props = withDefaults(defineProps<DataTableProps<T>>(), {
	canSelect: false,
	canSort: true,
	bordered: true,
	manualSorting: true,
	pageSizeOptions: () => [5, 10, DEFAULT_LISTING_PAGE_SIZE, 100, 1000],
	paginationOptions: () => ({
		getPaginationRowModel: getPaginationRowModel(),
	}),
});

const emit = defineEmits<DataTableEmits>();

const paginationState = defineModel<PaginationState>('pagination', {
	default: () => ({
		pageSize: DEFAULT_LISTING_PAGE_SIZE,
		pageIndex: 0,
	}),
});
const expandedState = defineModel<ExpandedState>('expanded', {
	default: () => ({}),
});

const forward = useForwardPropsEmits(
	reactiveOmit(
		props,
		'bordered',
		'canSelect',
		'canSort',
		'class',
		'columns',
		'columnOrder',
		'columnPinning',
		'data',
		'description',
		'disabled',
		'expanded',
		'hidePagination',
		'manualSorting',
		'onSelect',
		'pageSizeOptions',
		'pagination',
		'title',
	),
	emit,
);

const table = useTemplateRef<ComponentExposed<typeof DDataTableBase<T>>>('table');
const tableApi = computed(() => table.value?.tableApi);

// Constrain the column header menu to the table's scroll container. The trigger
// lives inside a horizontally-scrollable, overflow-clipped table; without a
// boundary, scrolling a column toward the edge makes floating-ui slam the menu
// to the far left of the viewport (over the sidebar) to keep it on screen.
const headerMenuBoundary = computed<HTMLElement | null>(() => table.value?.$el ?? null);

// Gap kept between the header menu and its collision boundary edges.
const HEADER_MENU_COLLISION_GAP = 8;

const { t } = useI18n();

const slots = useSlots();
const hasExpandedSlot = computed(() => !!slots.expanded);

const generatedColumns = useGeneratedColumns();
const CheckboxComponent = DCheckbox as Component;

const effectiveOnSelect = computed<DataTableBaseProps<T>['onSelect']>(() => {
	if (props.onSelect) {
		return props.onSelect;
	}

	return hasExpandedSlot.value ? (row: Row<T>) => row.toggleExpanded() : undefined;
});

watch(() => props.pageSizeOptions, () => {
	const pageSize = tableApi.value?.getState().pagination.pageSize;

	if (pageSize === undefined) {
		tableApi.value?.setPageSize(props.pageSizeOptions[0]!);
		return;
	}

	if (pageSize && !props.pageSizeOptions.includes(pageSize) && props.pageSizeOptions.length > 0) {
		tableApi.value?.setPageSize(props.pageSizeOptions[0]!);
	}
}, { immediate: true });

// Ensure that $-prefixed columns are added to the column order (and all special columns are stripped out)
const columnOrder = computed({
	get() {
		const prefix: string[] = [];
		if (props.canSelect) prefix.push('$select');
		if (hasExpandedSlot.value) prefix.push('$expand');

		if (prefix.length === 0) {
			return props.columnOrder;
		}

		return [...prefix, ...(props.columnOrder ?? [])];
	},
	set(columnOrder) {
		emit('update:columnOrder', columnOrder?.filter((id) => !id.startsWith('$')) ?? []);
	},
});

// Ensure that $-prefixed columns are added to the column pinning (and all special columns are stripped out)
const columnPinning = computed({
	get() {
		const left: string[] = [];
		if (props.canSelect) left.push('$select');
		if (hasExpandedSlot.value) left.push('$expand');

		if (left.length === 0) {
			return props.columnPinning;
		}

		return defu(
			{ left } as ColumnPinningState,
			props.columnPinning,
		);
	},
	set(columnPinning) {
		emit('update:columnPinning', {
			left: columnPinning?.left?.filter((id) => !id.startsWith('$')),
			right: columnPinning?.right?.filter((id) => !id.startsWith('$')),
		});
	},
});

defineExpose({ tableApi });

function useGeneratedColumns() {
	return computed(() => {
		const columns = (props.columns?.map((col, index) => ({
			...col,
			header: (context: HeaderContext<T, unknown>) => {
				return getHeader(context, col);
			},
			// The first user column sits right after the $expand trigger, so drop its start-padding to keep content tight.
			...(hasExpandedSlot.value && index === 0
				? {
						meta: {
							...col.meta,
							class: {
								...col.meta?.class,
								th: (header: any) => [callOrReturn(col.meta?.class?.th, header), 'ps-0'],
								td: (cell: any) => [callOrReturn(col.meta?.class?.td, cell), 'ps-0'],
							},
						},
					}
				: {}),
		})) ?? []) as NonNullable<DataTableBaseProps<T>['columns']>;

		if (hasExpandedSlot.value) {
			const expandRow = t('table.expand_row');
			const collapseRow = t('table.collapse_row');

			columns.unshift(<typeof columns[number]>{
				id: '$expand',
				enableHiding: false,
				enableResizing: false,
				enableOrdering: false,
				size: 32,
				minSize: 32,
				header: '',
				meta: {
					class: {
						th: 'border-r-0!',
						td: 'border-r-0! pe-3 group-data-[expanded=true]/row:border-b-0',
					},
				},
				cell: ({ row }) => h('div', { class: 'flex h-full w-full items-center justify-center' }, [
					h(DButton, {
						'size': 'xs',
						'color': 'neutral',
						'variant': 'link',
						'icon': 'stash:caret-right',
						'square': true,
						'class': 'size-4 min-h-4 min-w-4 p-0 text-dimmed',
						'aria-label': row.getIsExpanded() ? collapseRow : expandRow,
						'ui': {
							leadingIcon: [
								'transition-transform duration-75',
								row.getIsExpanded() ? 'rotate-90' : '',
							],
						},
						'onClick': (event: MouseEvent) => {
							event.stopPropagation();
							row.toggleExpanded();
						},
					}),
				]),
			});
		}

		if (props.canSelect) {
			const selectRow = t('table.select_row');
			const selectAll = t('table.select_all');

			columns.unshift(<typeof columns[number]>{
				id: '$select',
				enableHiding: false,
				enableResizing: false,
				enableOrdering: false,
				size: 48,
				minSize: 48,
				header: ({ table }) => h(CheckboxComponent, {
					'color': 'neutral',
					'modelValue': table.getIsSomePageRowsSelected() ? 'indeterminate' as const : table.getIsAllPageRowsSelected(),
					'onUpdate:modelValue': (value?: boolean | 'indeterminate') => table.toggleAllPageRowsSelected(!!value),
					'aria-label': selectAll,
					'ui': { base: 'group-hover/th:ring-(--ui-text-muted) ring-highlighted bg-default' },
				}),
				cell: ({ row }) => h(CheckboxComponent, {
					// set `id` to a falsy string to prevent global query selector: https://github.com/unovue/reka-ui/blob/a49f311d91a0591236d75428120b92fd5e2fdb87/packages/core/src/Checkbox/CheckboxRoot.vue#L107
					// TODO remove once PR is merged
					'id': '',
					'color': 'neutral',
					'modelValue': row.getIsSelected(),
					'onUpdate:modelValue': (value?: boolean | 'indeterminate') => row.toggleSelected(!!value),
					'aria-label': selectRow,
					'ui': { base: 'group-hover/cell:ring-highlighted bg-default' },
				}),
				meta: {
					onCellClick({ row }, e) {
						if (e?.target && 'tagName' in e.target && e.target?.tagName === 'BUTTON') {
							// checkbox clicked
							return;
						}

						row.toggleSelected();
						e?.stopPropagation();
						e?.preventDefault();
					},
				},
			});
		}

		return columns;
	});

	// eslint-disable-next-line unicorn/consistent-function-scoping
	function getHeader<TValue>(context: HeaderContext<T, TValue>, originalColumnDef: ExtendedTableColumn<T, TValue>) {
		const { column } = context;

		const canSort = props.canSort && originalColumnDef.enableSorting === true;
		const canHide = column.getCanHide();
		const headerActions = callOrReturn(originalColumnDef.meta?.headerActions, context) ?? [];

		if (!canSort && !canHide && headerActions.length === 0) {
			if (typeof originalColumnDef.header === 'function') {
				return (originalColumnDef.header as ((props: any) => any))(context);
			}

			return originalColumnDef.header ?? upperFirst(column.id);
		}

		const isSorted = column.getIsSorted();
		const canResize = column.getCanResize();
		const hasManualWidth = context.table.getState().columnSizing?.[column.id] != null;

		// Pad the menu off the sticky left-pinned region so a clamped/shifted menu
		// clears those columns instead of floating over them. Resolved against the
		// frozen render widths, since tanstack's getSize() can differ from what's
		// actually painted. getResolvedPinnedOffset sums the columns pinned before
		// this one: for a center column that's the full pinned-left width; for a
		// pinned column it's just the offset before it, so its own menu stays aligned.
		const leftPinnedInset = table.value?.getResolvedPinnedOffset(column, 'left') ?? 0;

		const sortGroup: DropdownMenuItem[] = canSort
			? [
					{
						label: t('table.asc'),
						type: 'checkbox' as const,
						icon: 'material-symbols:sort-rounded',
						ui: {
							itemLeadingIcon: '-scale-y-100',
						},
						checked: isSorted === 'asc',
						disabled: !canSort,
						onSelect: () => {
							if (isSorted === 'asc') {
								column.clearSorting();
							}
							else {
								column.toggleSorting(false);
							}
						},
					},
					{
						label: t('table.desc'),
						icon: 'material-symbols:sort-rounded',
						type: 'checkbox' as const,
						checked: isSorted === 'desc',
						disabled: !canSort,
						onSelect: () => {
							if (isSorted === 'desc') {
								column.clearSorting();
							}
							else {
								column.toggleSorting(true);
							}
						},
					},
				]
			: [];

		const visibilityGroup: DropdownMenuItem[] = [
			...(column.getCanPin()
				? [{
						label: column.getIsPinned() ? t('table.unpin_column') : t('table.pin_column'),
						icon: 'lucide:sidebar',
						disabled: !canHide,
						onSelect: () => {
							column.pin(column.getIsPinned() ? false : 'left');
						},
					}]
				: []),
			...(canResize && hasManualWidth
				? [{
						label: t('table.reset_width'),
						icon: 'lucide:unfold-horizontal',
						onSelect: () => {
							table.value?.resetColumnWidth(column.id);
						},
					}]
				: []),
			{
				label: t('table.hide_column'),
				icon: 'lucide:minus',
				disabled: !canHide,
				onSelect: () => {
					column.toggleVisibility(false);
				},
			},
		];

		return h(
			DDropdownMenu,
			{
				modal: false,
				content: {
					align: 'start',
					collisionBoundary: headerMenuBoundary.value,
					collisionPadding: {
						top: HEADER_MENU_COLLISION_GAP,
						right: HEADER_MENU_COLLISION_GAP,
						bottom: HEADER_MENU_COLLISION_GAP,
						left: HEADER_MENU_COLLISION_GAP + leftPinnedInset,
					},
					// Hide rather than float detached once the column scrolls fully out of view.
					hideWhenDetached: true,
				},
				items: buildColumnHeaderItemGroups(headerActions, [sortGroup, visibilityGroup]),
			},
			{
				default: () =>
					h(DButton, {
						size: 'sm',
						color: 'neutral',
						variant: 'link',
						label: typeof originalColumnDef.header === 'string' ? originalColumnDef.header ?? upperFirst(column.id) : undefined,
						trailingIcon: isSorted ? 'material-symbols:sort-rounded' : 'material-symbols:arrow-drop-down-rounded',
						class: 'min-w-full w-max group -mx-2.5 data-[state=open]:bg-elevated truncate',
						ui: {
							trailingIcon: [
								isSorted === 'asc' ? '-scale-y-100' : '',
								!isSorted ? 'absolute end-1.25 group-hover/th:inline group-data-[state=open]:inline hidden' : '',
								'size-4',
							],
						},
					}, (typeof originalColumnDef.header === 'function' ? (originalColumnDef.header as ((props: any) => any))(context) : undefined)),
			},
		);
	}
}
</script>

<template>
	<div
		class="flex flex-1 flex-col overflow-hidden aria-disabled:pointer-events-none aria-disabled:opacity-50"
		:class="{ 'rounded-sm border': bordered, [props.class]: props.class }"
		data-table
		:aria-disabled="disabled"
	>
		<slot v-if="!!$slots.header || !!$slots.actions" name="header">
			<DSecondaryHeader
				:description
				:title
				bar
			>
				<template #actions>
					<slot name="actions" />
				</template>
			</DSecondaryHeader>
		</slot>
		<DDataTableBase
			ref="table"
			v-bind="forward"
			v-model:column-order="columnOrder"
			v-model:column-pinning="columnPinning"
			v-model:expanded="expandedState"
			:pagination="hidePagination ? undefined : paginationState"
			:data
			:columns="generatedColumns"
			:on-select="effectiveOnSelect"
			:pagination-options="hidePagination ? {
				manualPagination: true,
			} : paginationOptions"
			:sorting-options="{ manualSorting }"
			:ui="mergeUi(props.ui, {
				root: 'flex-1',
			})"
			@update:pagination="(hidePagination || !$event) ? undefined : paginationState = $event"
		>
			<template v-if="$slots.expanded" #expanded="{ row }">
				<slot name="expanded" :row />
			</template>

			<template #empty>
				<slot name="empty" />
			</template>
		</DDataTableBase>
		<DPaginationBar
			v-if="!hidePagination"
			:page="(tableApi?.getState().pagination.pageIndex ?? 0) + 1"
			:page-size="tableApi?.getState().pagination.pageSize"
			:total-items="totalItems != null ? totalItems : tableApi?.getFilteredRowModel().rows.length"
			:page-size-options
			class="relative z-1 -mt-px border-t"
			@update:page="(p: number) => tableApi?.setPageIndex(p - 1)"
			@update:page-size="tableApi?.setPageSize"
		/>
		<slot name="footer" />
	</div>
</template>
